/**
 * SaveFlowDialog Component
 *
 * Dialog for saving a workflow flow configuration.
 * Allows user to name the flow and saves it with all node configurations and job IDs.
 */

import { useState } from 'react'
import { Save, Loader2 } from 'lucide-react'
import Modal, { FormField, FormInput, FormActions, Button } from '../shared/Modal'
import { saveFlow } from '../../services/flowService'
import './SaveFlowDialog.css'

function SaveFlowDialog({
  isOpen,
  onClose,
  onSaved,
  accountId,
  applicationName,
  flowData,
  jobMappings = {}
}) {
  const [flowName, setFlowName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleSave = async () => {
    if (!flowName.trim()) {
      setError('Please enter a flow name')
      return
    }

    if (!accountId || !applicationName) {
      setError('Account and application must be selected')
      return
    }

    if (!flowData || !flowData.nodes || flowData.nodes.length === 0) {
      setError('No nodes to save. Please build a workflow first.')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const result = await saveFlow(
        flowName.trim(),
        accountId,
        applicationName,
        flowData,
        jobMappings
      )

      console.log('Flow saved:', result)

      // Reset form
      setFlowName('')

      // Notify parent
      if (onSaved) {
        onSaved(result)
      }

      // Close dialog
      onClose()
    } catch (err) {
      console.error('Failed to save flow:', err)
      setError(err.message || 'Failed to save flow')
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    if (!saving) {
      setFlowName('')
      setError(null)
      onClose()
    }
  }

  const nodeCount = flowData?.nodes?.length || 0
  const completedJobs = jobMappings ? Object.values(jobMappings).filter(Boolean).length : 0

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="💾 Save Flow"
      size="medium"
      showClose={!saving}
    >
      <div className="save-flow-dialog">
        <FormField label="Flow Name" required error={error}>
          <FormInput
            value={flowName}
            onChange={setFlowName}
            placeholder="e.g., TestApp02 - Full Analysis"
            disabled={saving}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !saving) {
                handleSave()
              }
            }}
          />
        </FormField>

        <div className="flow-info">
          <div className="info-row">
            <span className="info-label">Account:</span>
            <span className="info-value">{accountId || 'Not set'}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Application:</span>
            <span className="info-value">{applicationName || 'Not set'}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Nodes:</span>
            <span className="info-value">
              {nodeCount === 0 ? (
                <span className="warning">⚠️ No nodes configured</span>
              ) : (
                <>✅ {nodeCount} configured</>
              )}
            </span>
          </div>
          <div className="info-row">
            <span className="info-label">Jobs:</span>
            <span className="info-value">
              {completedJobs === 0 ? (
                <span className="muted">No jobs run yet</span>
              ) : (
                <>✅ {completedJobs} completed</>
              )}
            </span>
          </div>
        </div>

        <FormActions>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !flowName.trim()}
          >
            {saving ? (
              <>
                <Loader2 size={16} className="spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Flow
              </>
            )}
          </Button>
        </FormActions>
      </div>
    </Modal>
  )
}

export default SaveFlowDialog
